import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { Button } from '@shared';

import merzLogo from '@/assets/Logo-Merz-Dental.svg';
import { useAuth } from '@/features/auth/useAuth';
import { displayName, organizationName } from '@/features/auth/user-display';
import { useTranslations } from '@/i18n/client';

// Merz Dental homepage URL.
const MERZ_HOMEPAGE_URL = 'https://www.merz-dental.de/m-denture';

export function AppHeader() {
  const t = useTranslations('common');
  const navigate = useNavigate();
  const { status, user, logout, busy } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const name = displayName(user);
  const org = organizationName(user);

  return (
    <header className="md-header sticky top-0 z-20 grid grid-cols-[auto_1fr] items-center gap-4 px-[max(1.5rem,calc((100%-var(--zs-container-width))/2))] py-4 max-lg:grid-cols-1 max-lg:items-start">
      <div className="flex min-h-12 min-w-12 items-center gap-5 leading-[1.2] max-lg:grid max-lg:grid-cols-[minmax(0,1fr)_auto] max-lg:gap-4">
        <Link
          to="/"
          className="flex shrink-0 items-center max-lg:col-start-1 max-lg:row-start-1"
          aria-label={t('nav.home')}
          onClick={() => setIsMenuOpen(false)}
        >
          <img src={merzLogo} alt={t('app.name')} className="block h-12 w-auto md-header__logo" />
        </Link>

        <button
          type="button"
          className="md-header__menu-toggle hidden h-11 w-11 items-center justify-center rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-white)] p-0 text-[var(--md-primary)] max-lg:col-start-2 max-lg:row-start-1 max-lg:inline-flex"
          aria-expanded={isMenuOpen}
          aria-controls="md-header-navigation"
          aria-label={t(isMenuOpen ? 'nav.closeMenu' : 'nav.openMenu')}
          title={t(isMenuOpen ? 'nav.closeMenu' : 'nav.openMenu')}
          onClick={() => setIsMenuOpen((current) => !current)}
        >
          {isMenuOpen ? (
            <XMarkIcon className="h-[1.4rem] w-[1.4rem]" aria-hidden="true" />
          ) : (
            <Bars3Icon className="h-[1.4rem] w-[1.4rem]" aria-hidden="true" />
          )}
        </button>

        <nav
          id="md-header-navigation"
          className={`flex items-center gap-3 max-lg:col-span-full max-lg:row-start-2 max-lg:w-full max-lg:flex-col max-lg:items-stretch max-lg:gap-0 max-lg:border-y max-lg:border-[var(--md-border)] max-lg:py-[0.35rem] ${isMenuOpen ? 'max-lg:flex' : 'max-lg:hidden'}`}
          aria-label={t('app.name')}
        >
          {status === 'authenticated' ? (
            <Link
              to="/"
              className="md-header__nav-link max-lg:py-[0.7rem]"
              onClick={() => setIsMenuOpen(false)}
            >
              {t('nav.dashboard')}
            </Link>
          ) : null}
          {status === 'authenticated' ? (
            <span className="md-header__nav-separator max-lg:hidden" aria-hidden="true">
              |
            </span>
          ) : null}
          <a
            href={MERZ_HOMEPAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="md-header__nav-link max-lg:py-[0.7rem]"
            onClick={() => setIsMenuOpen(false)}
          >
            {t('app.name')}
          </a>
        </nav>
      </div>

      {status === 'authenticated' ? (
        <div
          className={`flex items-center justify-self-end gap-3 max-lg:col-span-full max-lg:w-full max-lg:justify-self-stretch ${isMenuOpen ? 'max-lg:flex max-lg:flex-col max-lg:items-stretch max-lg:justify-start max-lg:flex-wrap max-lg:gap-3 max-lg:border-t max-lg:border-[var(--md-border)] max-lg:pt-3' : 'max-lg:hidden'}`}
        >
          {name || org ? (
            <div
              className={`flex flex-wrap items-center gap-2 ${isMenuOpen ? 'max-lg:flex-col max-lg:items-start max-lg:gap-1' : ''}`}
            >
              {name ? <span className="text-[0.9375rem]">{name}</span> : null}
              {org ? (
                <button
                  type="button"
                  className="md-header__org"
                  onClick={() => {
                    setIsMenuOpen(false);
                    navigate('/settings');
                  }}
                >
                  {org}
                </button>
              ) : null}
            </div>
          ) : null}
          <Button
            variant="secondary"
            className={isMenuOpen ? 'max-lg:w-full' : undefined}
            onClick={() => {
              setIsMenuOpen(false);
              void logout();
            }}
            loading={busy}
            loadingLabel={t('status.working')}
          >
            {t('nav.signOut')}
          </Button>
        </div>
      ) : null}
    </header>
  );
}
